import { useState } from "react";
import { mockSupportTickets } from "@/mocks/dealerData";

export default function SupportPage() {
  const [tickets, setTickets] = useState(mockSupportTickets);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: "", message: "" });
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);

  const createTicket = () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
    const ticket = {
      id: "st" + Date.now(),
      subject: newTicket.subject,
      message: newTicket.message,
      status: "open",
      date: new Date().toISOString(),
      replies: [],
    };
    setTickets((prev) => [ticket, ...prev]);
    setNewTicket({ subject: "", message: "" });
    setShowNewTicket(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="support-title">Destek Talepleri</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Sorun ve taleplerinizi iletin, size en kısa sürede dönüş yapalım</p>
        </div>
        <button onClick={() => setShowNewTicket(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Yeni Talep
        </button>
      </div>

      <div className="space-y-3">
        {tickets.map((ticket) => (
          <div key={ticket.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <div className="p-4 flex items-start justify-between cursor-pointer" onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ticket.status === "open" ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"}`}>
                    {ticket.status === "open" ? "Açık" : "Kapalı"}
                  </span>
                  <span className="text-xs text-foreground-400">{new Date(ticket.date).toLocaleDateString("tr-TR")} {new Date(ticket.date).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <h5 className="font-medium text-foreground-950 text-sm">{ticket.subject}</h5>
                <p className="text-sm text-foreground-500 mt-1 line-clamp-2">{ticket.message}</p>
              </div>
              <i className={`${expandedTicket === ticket.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-400 ml-3 mt-1`}></i>
            </div>

            {expandedTicket === ticket.id && (
              <div className="px-4 pb-4 border-t border-background-200/70 pt-3 space-y-3">
                {ticket.replies.length > 0 ? (
                  ticket.replies.map((reply, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-primary-600">
                          {reply.from === "admin" ? "Admin Yanıtı" : "Siz"}
                        </span>
                        <span className="text-xs text-foreground-400">{new Date(reply.date).toLocaleString("en-US")}</span>
                      </div>
                      <p className="text-sm text-foreground-700">{reply.message}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-foreground-400 text-center py-2">Henüz yanıtlanmadı</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {tickets.length === 0 && (
        <div className="text-center py-16 text-foreground-400">
          <i className="ri-customer-service-2-line text-4xl block mb-3"></i>
          <p className="text-sm">Henüz destek talebi yok.</p>
        </div>
      )}

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowNewTicket(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="new-ticket">Yeni Destek Talebi</a></h4>
              <button onClick={() => setShowNewTicket(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Konu *</label>
                <input type="text" value={newTicket.subject} onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="Talep konusu" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Mesaj *</label>
                <textarea value={newTicket.message} onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })} rows={4} maxLength={500} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none" placeholder="Sorununuzu detaylı açıklayın..."></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">{newTicket.message.length}/500</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowNewTicket(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={createTicket} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Gönder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}